// src/lib/cache/companies.js
//
// Per-entity cache wiring for companies. Singleton-ish — every company
// has exactly one row — but the rest of the app reads it as `companies.data`
// (an array), so we keep the list shape to match the existing accessor
// contract.
//
// Most settings sub-pages still write to the companies row via direct
// `supabase.from('companies').update(...)` calls and then call
// `refreshData('companies')`. That keeps working: refreshData invalidates
// `['companies', companyId]` (the same key this module reads), so the
// useQuery here refetches. New write sites should prefer the optimistic
// update mutation below.

import { supabase } from '../supabase.js'
import { getCompanies } from '../api/core/companies.js'
import {
  useEntityListQuery,
  useOptimisticListMutation,
  defaultUpdateReconcile,
  unwrapList,
} from './entityCache.js'

const ENTITY = 'companies'

const updateCompany = async ({ id, ...patch }) => {
  const { data, error } = await supabase
    .from('companies')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data
}

export const useCompaniesQuery = () =>
  useEntityListQuery(ENTITY, unwrapList('companies', getCompanies))

export const useUpdateCompanyMutation = () =>
  useOptimisticListMutation(ENTITY, {
    mutationFn: ({ id, ...patch }) => updateCompany({ id, ...patch }),
    applyOptimistic: (prev, vars) =>
      prev.map((row) => (row.id === vars.id ? { ...row, ...vars } : row)),
    reconcile: defaultUpdateReconcile,
  })
